import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, Download, AlertTriangle } from 'lucide-react';
import { useTrialStatus } from '../hooks/useTrialStatus';
import { useData } from '../context/DataContext';
import { useLanguage } from '../functions/useLanguage';

interface HardGateProps {
  children: React.ReactNode;
}

/**
 * Wraps the authenticated app shell.
 * – While loading / active / warning → renders children normally.
 * – expired_grace → full-screen paywall (data safe for N more days).
 * – expired      → full-screen expired screen (data gone).
 */
export function HardGate({ children }: HardGateProps) {
  const { phase, graceDaysLeft, trialEndDate } = useTrialStatus();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { logs, medications, contacts } = useData();

  // Pass-through for normal states
  if (phase === 'loading' || phase === 'active' || phase === 'warning') {
    return <>{children}</>;
  }

  const isFullyExpired = phase === 'expired';

  const handleExport = () => {
    // Build CSV with all log data (GDPR Article 20 portability)
    const moodLabels = ['Terrible', 'Not great', 'Okay', 'Good', 'Great'];

    const logRows = [
      ['Date', 'Heart Rate (bpm)', 'Resp Rate (bpm)', 'Weight (kg)', 'Fluid In (ml)', 'Temp (°C)', 'Mucous Membrane', 'Mood', 'Observations'],
      ...logs.map(l => [
        l.timestamp,
        (l as any).heart_rate_bpm ?? '',
        (l as any).resp_rate_bpm ?? '',
        l.weight_kg ?? '',
        l.fluid_in_ml ?? '',
        l.temp_c ?? '',
        (l as any).mucous_membrane ?? '',
        l.mood != null ? moodLabels[l.mood] ?? l.mood : '',
        l.observations_text ?? '',
      ]),
    ];

    const medRows = [
      [],
      ['--- Medications ---'],
      ['Name', 'Dosage', 'Timeslots'],
      ...medications.map(m => [m.name, m.dosage_mg, m.timeslots.join(' | ')]),
    ];

    const contactRows = [
      [],
      ['--- Contacts ---'],
      ['Name', 'Role', 'Phone', 'Email'],
      ...contacts.map(c => [c.name, c.role ?? '', c.phone_number ?? '', c.email ?? '']),
    ];

    const allRows = [...logRows, ...medRows, ...contactRows];
    const csv = allRows
      .map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(','))
      .join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `kodacare_export_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-[#f0f6ff] flex items-center justify-center p-6">
      <div className="bg-white rounded-3xl max-w-md w-full p-8 flex flex-col items-center text-center gap-6">

        {/* Icon */}
        <div
          className={`w-16 h-16 rounded-full flex items-center justify-center ${
            isFullyExpired ? 'bg-error-red\/10' : 'bg-amber-100'
          }`}
        >
          {isFullyExpired
            ? <AlertTriangle className="w-8 h-8 text-error-red" />
            : <Lock className="w-8 h-8 text-amber-500" />}
        </div>

        {/* Copy */}
        <div>
          <h2 className="text-xl font-black text-[#0d2744] mb-2">
            {isFullyExpired ? 'Account Expired' : 'Your Trial Has Ended'}
          </h2>
          <p className="text-text-secondary text-sm leading-relaxed">
            {isFullyExpired
              ? 'Your grace period has ended and your data has been removed. Subscribe to start fresh with KodaCare.'
              : `Your free trial ended on ${
                  trialEndDate?.toLocaleDateString('en-GB', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric',
                  }) ?? '—'
                }. Subscribe now to restore full access. Your data is safe for another ${graceDaysLeft} day${
                  graceDaysLeft === 1 ? '' : 's'
                }.`}
          </p>
        </div>

        {/* Primary CTA */}
        <button
          onClick={() => navigate('/website')}
          className="w-full bg-[#1976d2] hover:bg-hover-dark text-white font-bold py-4 rounded-full shadow-lg shadow-highlight-dark/30 transition-all hover:scale-[1.02] active:scale-95"
        >
          {t('trends.viewPlans')}
        </button>

        {/* Export (only available while data still exists) */}
        {!isFullyExpired && (
          <button
            onClick={handleExport}
            className="w-full flex items-center justify-center gap-2 border border-gray-200 text-text-secondary font-medium py-3 rounded-full hover:bg-gray-50 transition-all"
          >
            <Download className="w-4 h-4" />
            Export my data (CSV)
          </button>
        )}


      </div>
    </div>
  );
}
